import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Filter, X } from "lucide-react";

const LENGTHS = [
  { value: "", label: "All Lengths" },
  { value: "short", label: "Short (8–14″)" },
  { value: "medium", label: "Medium (16–22″)" },
  { value: "long", label: "Long (24–30+″)" },
];

const TEXTURES = ["Straight", "Body Wave", "Curly", "Deep Wave", "Water Wave", "Wave"];
const LACES = ["HD Lace", "Frontal", "Glueless", "Headband", "Closure"];

export default function Shop({ presetCategory }) {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filters = useMemo(() => ({
    category: presetCategory || params.get("category") || "",
    length: params.get("length") || "",
    texture: params.get("texture") || "",
    lace_type: params.get("lace_type") || "",
    sort: params.get("sort") || "featured",
    q: params.get("q") || "",
  }), [params, presetCategory]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (filters.category) q.category = filters.category;
    if (filters.length) q.length = filters.length;
    if (filters.texture) q.texture = filters.texture;
    if (filters.lace_type) q.lace_type = filters.lace_type;
    if (filters.sort) q.sort = filters.sort;
    if (filters.q) q.search = filters.q;
    api.get("/products", { params: q }).then((r) => {
      setProducts(r.data);
      setLoading(false);
    });
  }, [filters.category, filters.length, filters.texture, filters.lace_type, filters.sort, filters.q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const clearAll = () => setParams(new URLSearchParams(presetCategory ? "" : ""), { replace: true });

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
      <div className="mb-10">
        <p className="small-label">{filters.category || presetCategory ? "Collection" : "Shop"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3">
          {filters.q ? `Results for "${filters.q}"` : "All Wigs"}
        </h1>
        <p className="text-stone mt-3 max-w-xl">Filter by length, texture and lace to find your couture match.</p>
      </div>

      <div className="flex items-center justify-between gap-4 mb-8">
        <button
          data-testid="filters-toggle-button"
          onClick={() => setFiltersOpen((s) => !s)}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-luxe border border-stone-line rounded-full px-4 py-2.5 hover:bg-nude"
        >
          <Filter strokeWidth={1.5} className="w-4 h-4" /> Filters
        </button>
        <div className="flex items-center gap-3">
          <label className="small-label hidden sm:block">Sort</label>
          <select
            data-testid="shop-sort-select"
            value={filters.sort}
            onChange={(e) => setFilter("sort", e.target.value)}
            className="bg-ivory border border-stone-line rounded-full px-4 py-2.5 text-xs uppercase tracking-luxe focus:outline-none focus:ring-1 focus:ring-charcoal"
          >
            <option value="featured">Featured</option>
            <option value="best-selling">Best Selling</option>
            <option value="newest">Newest</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      {filtersOpen && (
        <div className="grid sm:grid-cols-3 gap-4 mb-10 p-6 border border-stone-line rounded-3xl bg-ivory fade-up">
          <div>
            <p className="small-label mb-2">Length</p>
            <div className="flex flex-wrap gap-2">
              {LENGTHS.map((l) => (
                <button
                  key={l.value}
                  data-testid={`filter-length-${l.value || "all"}`}
                  onClick={() => setFilter("length", l.value)}
                  className={`text-xs uppercase tracking-luxe rounded-full border px-3 py-1.5 ${
                    filters.length === l.value ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="small-label mb-2">Texture</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter("texture", "")} className={`text-xs uppercase tracking-luxe rounded-full border px-3 py-1.5 ${!filters.texture ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"}`}>All</button>
              {TEXTURES.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilter("texture", t)}
                  data-testid={`filter-texture-${t.toLowerCase().replace(/ /g, "-")}`}
                  className={`text-xs uppercase tracking-luxe rounded-full border px-3 py-1.5 ${
                    filters.texture === t ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="small-label mb-2">Lace</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFilter("lace_type", "")} className={`text-xs uppercase tracking-luxe rounded-full border px-3 py-1.5 ${!filters.lace_type ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"}`}>All</button>
              {LACES.map((l) => (
                <button
                  key={l}
                  onClick={() => setFilter("lace_type", l)}
                  data-testid={`filter-lace-${l.toLowerCase().replace(/ /g, "-")}`}
                  className={`text-xs uppercase tracking-luxe rounded-full border px-3 py-1.5 ${
                    filters.lace_type === l ? "bg-charcoal text-ivory border-charcoal" : "border-stone-line"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="clear-filters-button" onClick={clearAll} className="text-xs uppercase tracking-luxe text-stone hover:text-charcoal inline-flex items-center gap-1 sm:col-span-3 justify-self-end">
            <X strokeWidth={1.5} className="w-4 h-4" /> Clear filters
          </button>
        </div>
      )}

      {loading ? (
        <p data-testid="shop-loading" className="text-center py-20 small-label">Loading…</p>
      ) : products.length === 0 ? (
        <p data-testid="shop-empty" className="text-center py-20">No wigs match your filters.</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 lg:gap-x-8 gap-y-10">
          {products.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      )}
      <p className="text-center small-label mt-12 text-stone">{products.length} pieces · Curated in South Africa</p>
    </div>
  );
}
